export const ONBOARDING_COPY = {
  en: {
    eyebrow: 'WORKPLANSIQ ONBOARDING',
    title: 'Set up your subscription',
    description: 'Complete the commercial setup before your order and invoice are created.',
    steps: ['Plan', 'Billing details', 'Review order'],
    shellSteps: [
      {
        title: 'Review your selected plan',
        description: 'Confirm the plan, capacity, and duration received from WorkPlansIQ.com.',
      },
      {
        title: 'Billing details',
        description: 'Provide the legal entity and contact details that will receive the invoice for this order.',
      },
      {
        title: 'Review your order',
        description: 'Review all commercial details before creating the order.',
      },
    ],
    choosePlanStep: {
      title: 'Choose your plan',
      description: 'Select the plan that best matches your workforce planning needs.',
    },
    choosePlanPlaceholder: 'Plan selection will be implemented in the next stage.',
    planSelection: {
      loadError: 'We could not load the available plans.',
      retry: 'Try again',
      empty: 'No active plans are available.',
      select: 'Select plan',
      selected: 'Selected',
      contactSales: 'Contact sales',
      capacity: 'Capacity / license units',
      duration: 'Duration',
      units: 'units',
      months: 'months',
      configureTitle: 'Configure your plan',
      configureDescription: 'Confirm the license capacity and subscription duration before continuing.',
    },
    stepProgress: (current, total) => `Step ${current} of ${total}`,
    completed: 'Complete',
    organizationSetupProgress: 'Organization setup',
    provisioningProgress: 'Provisioning',
    inviteMembersProgress: 'Invite members',
    stepLabel: (step) => `STEP ${step}`,
    placeholder: 'The form for this step will be implemented in the next stage.',
    back: 'Back',
    continueLabel: 'Continue',
    shellComplete: 'Ready for review',
    logout: 'Logout',
    continueBilling: 'Continue to billing',
    reviewDetails: 'Review details',
    confirmOrder: 'Confirm order',
    invalidTitle: 'Invalid onboarding link',
    invalidDescription: 'Plan, capacity, duration, or locale is missing or invalid.',
    returnToPlans: 'Return to plans',
    errors: {
      billingApi: 'Billing Account API is not configured.',
      selectAccount: 'Select a billing account before continuing.',
    },
    plan: {
      title: 'Review your selected plan',
      description: 'This selection came from WorkPlansIQ.com and will be retained during onboarding.',
      bypassTitle: 'Development authentication bypass is active',
      bypassDescription: 'Login is skipped only in this local environment while the authentication API is unavailable.',
      labels: { plan: 'Plan', capacity: 'Capacity', duration: 'Duration', language: 'Language' },
      units: 'units',
      months: 'months',
    },
    billing: {
      title: 'Choose a billing account',
      description: 'A billing account stores the legal entity and invoice delivery details. It does not grant access to project data.',
      billingInformation: 'Billing information',
      invoicePreview: 'Invoice preview',
      invoicePreviewDescription: 'This is how the billing information will appear on your invoice.',
      previewRecipient: 'Invoice recipient',
      previewEmpty: '—',
      useExisting: 'Use existing billing account',
      createNew: 'Create new billing account',
      noAccount: 'No billing account is available',
      createAccount: 'Create billing account',
      legalName: 'Billing name',
      legalNamePlaceholder: 'PT Nusantara Utama',
      legalNameRequired: 'Enter the billing name.',
      taxId: 'Tax ID / NPWP',
      optional: 'Optional',
      address: 'Billing address',
      addressRequired: 'Enter the billing address.',
      city: 'City',
      cityRequired: 'Enter the city.',
      postalCode: 'Postal code',
      postalCodeRequired: 'Enter the postal code.',
      country: 'Country',
      countryRequired: 'Enter the country.',
      contact: 'Billing contact name',
      contactRequired: 'Enter the billing contact name.',
      invoiceEmail: 'Invoice delivery email',
      invoiceEmailHelp: 'Invoices and payment reminders will be sent to this email.',
      invoiceEmailRequired: 'Enter the invoice email.',
      invoiceEmailInvalid: 'Enter a valid email address.',
      phoneNumber: 'Billing phone number',
      phoneNumberRequired: 'Enter the billing phone number.',
      companyRegistrationNumber: 'Company registration number',
      formNote: 'This information will be used for invoice delivery. You can review it before confirming the order.',
    },
    review: {
      title: 'Review onboarding details',
      description: 'Check the selected package and billing information before requesting a final quotation.',
      planSection: 'Plan information',
      billingSection: 'Billing information',
      edit: 'Edit',
      notAvailable: 'Not available',
      units: 'units',
      months: 'months',
      labels: {
        plan: 'Plan', capacity: 'Capacity', duration: 'Duration',
        price: 'Pricing', billingEntity: 'Billing entity', address: 'Billing address',
        contact: 'Billing contact name', invoiceEmail: 'Invoice email', phone: 'Billing phone number',
        taxId: 'Tax ID / NPWP', dataSource: 'Data source',
      },
      mock: 'Development mock',
      apiTitle: 'Final pricing and order confirmation require the billing API',
      apiDescription: 'No order, invoice, license, or 14-day access will be created from this development screen.',
      termsAgreement: 'I confirm that the plan and billing information above are correct.',
      termsDescription: 'Order creation will also require acceptance of the Terms of Service and Privacy Policy when the billing API is integrated.',
    },
    confirmation: {
      eyebrow: 'ONBOARDING PREVIEW COMPLETE',
      title: 'Order confirmation preview',
      description: 'Your plan and billing information are ready to be submitted when the order API is available.',
      mockTitle: 'Development preview only',
      mockDescription: 'No order, invoice, payment deadline, license, or 14-day access has been created.',
      summaryTitle: 'Submission summary',
      previewTag: 'Mock',
      notAvailable: 'Not available',
      pendingApi: 'Pending API integration',
      units: 'units',
      months: 'months',
      labels: {
        plan: 'Plan', billing: 'Billing name', capacity: 'Capacity', duration: 'Duration',
        order: 'Order number', invoice: 'Invoice number',
      },
      backToReview: 'Back to review',
      continueSetup: 'Continue setup',
    },
    organizationSetup: {
      eyebrow: 'ORGANIZATION SETUP',
      title: 'Set up your organization',
      description: 'Confirm the organization information and choose who will receive Organization Administrator access.',
      name: 'Organization name',
      namePlaceholder: 'PT Cerdas Inovasi',
      nameRequired: 'Enter the organization name.',
      address: 'Organization address',
      contactPerson: 'Contact person',
      contactPersonRequired: 'Enter the organization contact person.',
      contactNumber: 'Contact number',
      contactNumberRequired: 'Enter the organization contact number.',
      efficiencyFactor: 'Efficiency factor',
      efficiencyFactorHelp: 'Existing WLA calculations require a value between 65% and 100%.',
      efficiencyFactorRequired: 'Enter the efficiency factor.',
      efficiencyFactorRange: 'Efficiency factor must be between 65 and 100.',
      adminTitle: 'Organization Administrator',
      adminDescription: 'Choose who will administer this organization after provisioning.',
      currentAdmin: 'I will be the Organization Administrator',
      inviteAdmin: 'Invite another Organization Administrator',
      adminEmail: 'Organization Administrator email',
      adminEmailRequired: 'Enter the Organization Administrator email.',
      adminEmailInvalid: 'Enter a valid email address.',
      back: 'Back',
      save: 'Save organization setup',
      update: 'Update setup',
      startProvisioning: 'Start provisioning',
      savedTitle: 'Organization setup saved',
      savedDescription: 'This mock setup is ready for the provisioning UI. No organization or membership has been created.',
    },
    provisioning: {
      processingTitle: 'Setting up your organization...',
      processingDescription: 'This mock process previews the provisioning states. Please do not close the page.',
      processingNote: 'No backend resources are being created during this development preview.',
      steps: [
        { title: 'Creating organization', description: 'Setting up your dedicated organization environment' },
        { title: 'Creating organization', description: 'Applying organization information and WLA defaults' },
        { title: 'Assigning plan', description: 'Preparing plan capacity and duration' },
        { title: 'Assigning Organization Administrator', description: 'Preparing the administrator membership or invitation' },
        { title: 'Finalizing organization', description: 'Completing the initial setup' },
      ],
      simulateFailure: 'Simulate failure (development)',
      successTitle: 'Your organization is ready!',
      successDescription: 'The organization setup preview has completed successfully.',
      organization: 'Organization',
      plan: 'Plan',
      admin: 'Organization Administrator',
      membership: 'Membership status',
      currentUser: 'Current signed-in user',
      active: 'Active',
      invitationPending: 'Invitation pending',
      mockSuccessTitle: 'Development preview only',
      mockSuccessDescription: 'No organization, entitlement, membership, or email invitation has been created.',
      failedTitle: "We couldn't set up your organization",
      failedDescription: 'The provisioning preview stopped before setup was completed.',
      errorReference: 'Error reference',
      errorDescription: 'Mock provisioning failure. No backend data was changed.',
      tryAgain: 'Try again',
      backToSetup: 'Back to organization setup',
      inviteMembers: 'Invite team members',
      goToDashboard: 'Go to Dashboard',
    },
    inviteMembers: {
      eyebrow: 'TEAM SETUP',
      title: 'Invite team members',
      description: 'Add members who should access this organization. You can also skip this step and invite them later.',
      email: 'Work email',
      emailRequired: 'Enter the member email.',
      emailInvalid: 'Enter a valid email address.',
      duplicateEmail: 'This email has already been added.',
      role: 'Role',
      roles: [
        { value: 'tenant-admin', label: 'Organization Administrator' },
        { value: 'reviewer', label: 'Reviewer' },
        { value: 'submitter', label: 'Submitter' },
        { value: 'member', label: 'Member' },
      ],
      roleLabel: (role) => ({
        'tenant-admin': 'Organization Administrator', reviewer: 'Reviewer', submitter: 'Submitter', member: 'Member',
      }[role] || role),
      add: 'Add member',
      listTitle: 'Members to invite',
      memberCount: (count) => `${count} member${count === 1 ? '' : 's'}`,
      empty: 'No members have been added.',
      remove: 'Remove member',
      mockNotice: 'Invitations are saved only for this development preview. No email will be sent.',
      savedTitle: 'Invitation list saved',
      savedDescription: 'The mock invitation list has been retained for UI review.',
      back: 'Back',
      skip: 'Skip for now',
      send: 'Save invitations',
      goToDashboard: 'Go to Dashboard',
    },
    planNames: {
      'wla-sprint': 'WLA Diagnostic Sprint',
      'workforce-intelligence': 'Continuous Workforce Intelligence',
    },
  },
  id: {
    eyebrow: 'ONBOARDING WORKPLANSIQ',
    title: 'Siapkan langganan Anda',
    description: 'Lengkapi pengaturan komersial sebelum order dan invoice dibuat.',
    steps: ['Paket', 'Informasi penagihan', 'Tinjau order'],
    shellSteps: [
      {
        title: 'Tinjau paket yang dipilih',
        description: 'Konfirmasi paket, kapasitas, dan durasi yang diterima dari WorkPlansIQ.com.',
      },
      {
        title: 'Informasi penagihan',
        description: 'Masukkan badan usaha dan informasi kontak yang akan menerima invoice untuk order ini.',
      },
      {
        title: 'Tinjau order Anda',
        description: 'Periksa seluruh informasi komersial sebelum membuat order.',
      },
    ],
    choosePlanStep: {
      title: 'Pilih paket Anda',
      description: 'Pilih paket yang paling sesuai dengan kebutuhan perencanaan tenaga kerja Anda.',
    },
    choosePlanPlaceholder: 'Pemilihan paket akan dikerjakan pada tahap berikutnya.',
    planSelection: {
      loadError: 'Paket yang tersedia tidak dapat dimuat.',
      retry: 'Coba lagi',
      empty: 'Belum ada paket aktif yang tersedia.',
      select: 'Pilih paket',
      selected: 'Dipilih',
      contactSales: 'Hubungi sales',
      capacity: 'Kapasitas / unit lisensi',
      duration: 'Durasi',
      units: 'unit',
      months: 'bulan',
      configureTitle: 'Atur paket Anda',
      configureDescription: 'Konfirmasi kapasitas lisensi dan durasi langganan sebelum melanjutkan.',
    },
    stepProgress: (current, total) => `Langkah ${current} dari ${total}`,
    completed: 'Selesai',
    organizationSetupProgress: 'Pengaturan organisasi',
    provisioningProgress: 'Provisioning',
    inviteMembersProgress: 'Undang anggota',
    stepLabel: (step) => `LANGKAH ${step}`,
    placeholder: 'Form untuk langkah ini akan dikerjakan pada tahap berikutnya.',
    back: 'Kembali',
    continueLabel: 'Lanjutkan',
    shellComplete: 'Siap ditinjau',
    logout: 'Keluar',
    continueBilling: 'Lanjut ke penagihan',
    reviewDetails: 'Tinjau informasi',
    confirmOrder: 'Konfirmasi order',
    invalidTitle: 'Tautan onboarding tidak valid',
    invalidDescription: 'Paket, kapasitas, durasi, atau bahasa tidak tersedia atau tidak valid.',
    returnToPlans: 'Kembali ke pilihan paket',
    errors: {
      billingApi: 'API akun penagihan belum dikonfigurasi.',
      selectAccount: 'Pilih akun penagihan sebelum melanjutkan.',
    },
    plan: {
      title: 'Tinjau paket yang dipilih',
      description: 'Pilihan ini berasal dari WorkPlansIQ.com dan akan dipertahankan selama onboarding.',
      bypassTitle: 'Bypass autentikasi development sedang aktif',
      bypassDescription: 'Login hanya dilewati di lingkungan lokal ini selama API autentikasi belum tersedia.',
      labels: { plan: 'Paket', capacity: 'Kapasitas', duration: 'Durasi', language: 'Bahasa' },
      units: 'unit',
      months: 'bulan',
    },
    billing: {
      title: 'Pilih akun penagihan',
      description: 'Akun penagihan menyimpan informasi badan usaha dan pengiriman invoice. Akun ini tidak memberikan akses ke data proyek.',
      billingInformation: 'Informasi penagihan',
      invoicePreview: 'Pratinjau invoice',
      invoicePreviewDescription: 'Informasi penagihan akan ditampilkan pada invoice seperti berikut.',
      previewRecipient: 'Penerima invoice',
      previewEmpty: '—',
      useExisting: 'Gunakan akun penagihan yang ada',
      createNew: 'Buat akun penagihan baru',
      noAccount: 'Belum ada akun penagihan',
      createAccount: 'Buat akun penagihan',
      legalName: 'Nama penagihan',
      legalNamePlaceholder: 'PT Nusantara Utama',
      legalNameRequired: 'Masukkan nama penagihan.',
      taxId: 'NPWP / Nomor pajak',
      optional: 'Opsional',
      address: 'Alamat penagihan',
      addressRequired: 'Masukkan alamat penagihan.',
      city: 'Kota',
      cityRequired: 'Masukkan kota.',
      postalCode: 'Kode pos',
      postalCodeRequired: 'Masukkan kode pos.',
      country: 'Negara',
      countryRequired: 'Masukkan negara.',
      contact: 'Nama kontak penagihan',
      contactRequired: 'Masukkan nama kontak penagihan.',
      invoiceEmail: 'Email penerima invoice',
      invoiceEmailHelp: 'Invoice dan pengingat pembayaran akan dikirim ke email ini.',
      invoiceEmailRequired: 'Masukkan email penerima invoice.',
      invoiceEmailInvalid: 'Masukkan alamat email yang valid.',
      phoneNumber: 'Nomor telepon penagihan',
      phoneNumberRequired: 'Masukkan nomor telepon penagihan.',
      companyRegistrationNumber: 'Nomor registrasi perusahaan',
      formNote: 'Informasi ini akan digunakan untuk pengiriman invoice. Anda dapat meninjaunya sebelum mengonfirmasi order.',
    },
    review: {
      title: 'Tinjau informasi onboarding',
      description: 'Periksa paket dan informasi penagihan sebelum meminta penawaran harga final.',
      planSection: 'Informasi paket',
      billingSection: 'Informasi penagihan',
      edit: 'Ubah',
      notAvailable: 'Tidak tersedia',
      units: 'unit',
      months: 'bulan',
      labels: {
        plan: 'Paket', capacity: 'Kapasitas', duration: 'Durasi',
        price: 'Harga', billingEntity: 'Badan usaha', address: 'Alamat penagihan',
        contact: 'Nama kontak penagihan', invoiceEmail: 'Email invoice', phone: 'Nomor telepon penagihan',
        taxId: 'NPWP / Nomor pajak', dataSource: 'Sumber data',
      },
      mock: 'Mock development',
      apiTitle: 'Harga final dan konfirmasi order memerlukan API penagihan',
      apiDescription: 'Layar development ini tidak akan membuat order, invoice, lisensi, atau akses awal 14 hari.',
      termsAgreement: 'Saya mengonfirmasi bahwa informasi paket dan penagihan di atas sudah benar.',
      termsDescription: 'Pembuatan order juga akan memerlukan persetujuan Ketentuan Layanan dan Kebijakan Privasi setelah API penagihan terintegrasi.',
    },
    confirmation: {
      eyebrow: 'PRATINJAU ONBOARDING SELESAI',
      title: 'Pratinjau konfirmasi order',
      description: 'Informasi paket dan penagihan siap dikirim setelah API order tersedia.',
      mockTitle: 'Hanya pratinjau development',
      mockDescription: 'Belum ada order, invoice, batas pembayaran, lisensi, atau akses 14 hari yang dibuat.',
      summaryTitle: 'Ringkasan pengiriman',
      previewTag: 'Mock',
      notAvailable: 'Tidak tersedia',
      pendingApi: 'Menunggu integrasi API',
      units: 'unit',
      months: 'bulan',
      labels: {
        plan: 'Paket', billing: 'Nama penagihan', capacity: 'Kapasitas', duration: 'Durasi',
        order: 'Nomor order', invoice: 'Nomor invoice',
      },
      backToReview: 'Kembali ke tinjauan',
      continueSetup: 'Lanjutkan pengaturan',
    },
    organizationSetup: {
      eyebrow: 'PENGATURAN ORGANISASI',
      title: 'Siapkan organisasi Anda',
      description: 'Konfirmasi informasi organisasi dan pilih pengguna yang akan memperoleh akses Administrator Organisasi.',
      name: 'Nama organisasi',
      namePlaceholder: 'PT Cerdas Inovasi',
      nameRequired: 'Masukkan nama organisasi.',
      address: 'Alamat organisasi',
      contactPerson: 'Kontak organisasi',
      contactPersonRequired: 'Masukkan kontak organisasi.',
      contactNumber: 'Nomor kontak',
      contactNumberRequired: 'Masukkan nomor kontak organisasi.',
      efficiencyFactor: 'Faktor efisiensi',
      efficiencyFactorHelp: 'Perhitungan WLA existing membutuhkan nilai antara 65% dan 100%.',
      efficiencyFactorRequired: 'Masukkan faktor efisiensi.',
      efficiencyFactorRange: 'Faktor efisiensi harus antara 65 dan 100.',
      adminTitle: 'Administrator Organisasi',
      adminDescription: 'Pilih pengguna yang akan mengelola organisasi setelah provisioning.',
      currentAdmin: 'Saya akan menjadi Administrator Organisasi',
      inviteAdmin: 'Undang Administrator Organisasi lain',
      adminEmail: 'Email Administrator Organisasi',
      adminEmailRequired: 'Masukkan email Administrator Organisasi.',
      adminEmailInvalid: 'Masukkan alamat email yang valid.',
      back: 'Kembali',
      save: 'Simpan pengaturan organisasi',
      update: 'Perbarui pengaturan',
      startProvisioning: 'Mulai provisioning',
      savedTitle: 'Pengaturan organisasi tersimpan',
      savedDescription: 'Data mock ini siap digunakan pada UI provisioning. Belum ada organisasi atau membership yang dibuat.',
    },
    provisioning: {
      processingTitle: 'Menyiapkan organisasi Anda...',
      processingDescription: 'Proses mock ini menampilkan state provisioning. Jangan tutup halaman ini.',
      processingNote: 'Tidak ada resource backend yang dibuat selama pratinjau development ini.',
      steps: [
        { title: 'Membuat organisasi', description: 'Menyiapkan lingkungan organisasi khusus' },
        { title: 'Membuat organisasi', description: 'Menerapkan informasi organisasi dan default WLA' },
        { title: 'Memasang paket', description: 'Menyiapkan kapasitas dan durasi paket' },
        { title: 'Menetapkan Administrator Organisasi', description: 'Menyiapkan membership administrator atau undangan' },
        { title: 'Finalisasi organisasi', description: 'Menyelesaikan pengaturan awal' },
      ],
      simulateFailure: 'Simulasikan kegagalan (development)',
      successTitle: 'Organisasi Anda siap!',
      successDescription: 'Pratinjau pengaturan organisasi berhasil diselesaikan.',
      organization: 'Organisasi',
      plan: 'Paket',
      admin: 'Administrator Organisasi',
      membership: 'Status membership',
      currentUser: 'Pengguna yang sedang login',
      active: 'Aktif',
      invitationPending: 'Undangan menunggu',
      mockSuccessTitle: 'Hanya pratinjau development',
      mockSuccessDescription: 'Belum ada organisasi, entitlement, membership, atau undangan email yang dibuat.',
      failedTitle: 'Organisasi tidak dapat disiapkan',
      failedDescription: 'Pratinjau provisioning berhenti sebelum pengaturan selesai.',
      errorReference: 'Referensi error',
      errorDescription: 'Kegagalan provisioning mock. Tidak ada data backend yang berubah.',
      tryAgain: 'Coba lagi',
      backToSetup: 'Kembali ke pengaturan organisasi',
      inviteMembers: 'Undang anggota tim',
      goToDashboard: 'Buka Dashboard',
    },
    inviteMembers: {
      eyebrow: 'PENGATURAN TIM',
      title: 'Undang anggota tim',
      description: 'Tambahkan anggota yang akan mengakses organisasi ini. Langkah ini dapat dilewati dan dilakukan nanti.',
      email: 'Email kerja',
      emailRequired: 'Masukkan email anggota.',
      emailInvalid: 'Masukkan alamat email yang valid.',
      duplicateEmail: 'Email ini sudah ditambahkan.',
      role: 'Peran',
      roles: [
        { value: 'tenant-admin', label: 'Administrator Organisasi' },
        { value: 'reviewer', label: 'Reviewer' },
        { value: 'submitter', label: 'Submitter' },
        { value: 'member', label: 'Member' },
      ],
      roleLabel: (role) => ({
        'tenant-admin': 'Administrator Organisasi', reviewer: 'Reviewer', submitter: 'Submitter', member: 'Member',
      }[role] || role),
      add: 'Tambah anggota',
      listTitle: 'Anggota yang akan diundang',
      memberCount: (count) => `${count} anggota`,
      empty: 'Belum ada anggota yang ditambahkan.',
      remove: 'Hapus anggota',
      mockNotice: 'Undangan hanya disimpan untuk pratinjau development. Tidak ada email yang dikirim.',
      savedTitle: 'Daftar undangan tersimpan',
      savedDescription: 'Daftar undangan mock telah disimpan untuk review UI.',
      back: 'Kembali',
      skip: 'Lewati untuk sekarang',
      send: 'Simpan undangan',
      goToDashboard: 'Buka Dashboard',
    },
    planNames: {
      'wla-sprint': 'WLA Diagnostic Sprint',
      'workforce-intelligence': 'Continuous Workforce Intelligence',
    },
  },
};
